import { useRouter } from "@shared/client/navigation";
import { useState } from "react";
import { Check, Copy, Flame, LoaderCircle, PauseCircle, Play, Plus, RotateCcw, Send, ShieldCheck, Trash2 } from "lucide-react";
import { MAILBOX_PHASE_LABELS, type MailboxOverview } from "@shared/lib/prospection/shared";
import { formatDate, formatDateTime, formatNumber } from "@shared/lib/format";
import { AdminChip, AdminEmpty, AdminMeta, AdminPanel } from "@shared/components/blocks";
import { Alert, AlertDescription } from "@shared/components/ui/alert";
import { Button } from "@shared/components/ui/button";
import { Input } from "@shared/components/ui/input";
import { Label } from "@shared/components/ui/label";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@shared/components/ui/table";

/**
 * Boîtes d'envoi : des utilisateurs d'une organisation Google Workspace
 * dédiée à la prospection, sur des domaines distincts du domaine principal.
 * Chacune chauffe (deux semaines par défaut, durée dans les réglages), monte en charge, et se met en pause seule
 * quand ses rebonds ou ses désinscriptions dépassent les seuils. « Rester en
 * chauffe » la retient sans prospection aussi longtemps qu'on veut ;
 * « Lancer la prospection » la relâche à la semaine 3 de la montée en charge.
 */

export type DnsReport = {
  domain: string;
  spf: { ok: boolean; hint: string };
  dkim: { ok: boolean; hint: string };
  dmarc: { ok: boolean; hint: string };
};

const PHASE_TONES = { warmup: "trial", held: "trial", active: "active", paused: "churned" } as const;

const SCOPES = "https://www.googleapis.com/auth/gmail.send,https://www.googleapis.com/auth/gmail.modify";

async function api(path: string, method: string, body?: unknown) {
  const response = await fetch(path, {
    method,
    headers: { "Content-Type": "application/json" },
    body: body === undefined ? undefined : JSON.stringify(body),
  });
  const data = await response.json().catch(() => ({}));
  if (!response.ok) throw new Error((data as { error?: string }).error || "Opération impossible.");
  return data;
}

export function MailboxesView({
  mailboxes,
  dns,
  setup,
}: {
  mailboxes: MailboxOverview[];
  dns: DnsReport[];
  setup: { configured: boolean; clientId: string | null };
}) {
  const router = useRouter();
  const [busy, setBusy] = useState("");
  const [error, setError] = useState("");

  async function act(key: string, action: () => Promise<unknown>) {
    setBusy(key);
    setError("");
    try {
      await action();
      router.refresh();
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "Opération impossible.");
    } finally {
      setBusy("");
    }
  }

  const capacity = mailboxes.reduce((total, mailbox) => total + mailbox.capacityToday, 0);
  const sent = mailboxes.reduce((total, mailbox) => total + mailbox.sentToday, 0);

  return (
    <div className="grid gap-4.5">
      <AdminMeta note={setup.configured ? "Compte de service Google configuré." : "Compte de service Google absent : rien ne peut partir."}>
        {mailboxes.length === 0
          ? "Aucune boîte d’envoi."
          : `${mailboxes.length} boîte${mailboxes.length > 1 ? "s" : ""} · ${formatNumber(sent)} e-mail(s) de séquence envoyé(s) aujourd’hui sur une capacité de ${formatNumber(capacity)}`}
      </AdminMeta>

      {error && (
        <Alert role="alert" variant="destructive">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {(!setup.configured || mailboxes.length === 0) && <SetupPanel clientId={setup.clientId} configured={setup.configured} />}

      <AdminPanel flush title="Boîtes">
        {mailboxes.length === 0 ? (
          <AdminEmpty>Ajoutez une première boîte : elle commence par sa chauffe, sans prospection (durée dans les réglages).</AdminEmpty>
        ) : (
          <Table>
            <TableHeader>
              <TableRow className="hover:bg-transparent">
                <TableHead className="pl-5">Boîte</TableHead>
                <TableHead>État</TableHead>
                <TableHead className="max-sm:hidden">Aujourd’hui</TableHead>
                <TableHead className="max-sm:hidden">Sur 7 jours</TableHead>
                <TableHead className="max-sm:hidden">Accès</TableHead>
                <TableHead className="pr-5 text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {mailboxes.map((mailbox) => (
                <TableRow key={mailbox.id}>
                  <TableCell className="pl-5">
                    <span className="font-semibold">{mailbox.email}</span>
                    <small className="block text-[12px] text-muted-foreground">
                      {mailbox.display_name}
                      {mailbox.daily_cap ? ` · plafond ${mailbox.daily_cap}` : ""}
                    </small>
                  </TableCell>
                  <TableCell className="max-w-[260px]">
                    <AdminChip tone={PHASE_TONES[mailbox.phase]}>{MAILBOX_PHASE_LABELS[mailbox.phase]}</AdminChip>
                    <small className="block text-[12px] text-muted-foreground">
                      {mailbox.phase === "paused"
                        ? mailbox.pause_reason ?? "Mise en pause à la main"
                        : mailbox.phase === "held"
                          ? `semaine ${mailbox.warmupWeek} · aucune prospection tant qu’elle est retenue`
                          : `semaine ${mailbox.warmupWeek} depuis le ${formatDate(mailbox.warmup_started_on)}`}
                    </small>
                  </TableCell>
                  <TableCell className="tabular-nums whitespace-nowrap max-sm:hidden">
                    {mailbox.sentToday} / {mailbox.capacityToday}
                    {mailbox.next_send_at && new Date(mailbox.next_send_at) > new Date() && (
                      <small className="block text-[12px] text-muted-foreground">prochain après {formatDateTime(mailbox.next_send_at)}</small>
                    )}
                  </TableCell>
                  <TableCell className="tabular-nums whitespace-nowrap max-sm:hidden">
                    {mailbox.sent7d} envoyé(s)
                    <small className="block text-[12px] text-muted-foreground">
                      {mailbox.bounces7d} rebond(s) · {mailbox.unsubscribes7d} désinscription(s)
                    </small>
                  </TableCell>
                  <TableCell className="max-w-[220px] max-sm:hidden">
                    {mailbox.access_error ? (
                      <span className="text-[12.5px] text-destructive">{mailbox.access_error}</span>
                    ) : mailbox.access_verified_at ? (
                      <span className="text-[12.5px] text-muted-foreground">vérifié le {formatDate(mailbox.access_verified_at)}</span>
                    ) : (
                      "—"
                    )}
                  </TableCell>
                  <TableCell className="pr-5">
                    <span className="flex flex-wrap justify-end gap-1.5">
                      {mailbox.status === "paused" ? (
                        <Button
                          disabled={busy === `resume-${mailbox.id}`}
                          onClick={() => act(`resume-${mailbox.id}`, () => api(`/api/admin/prospection/mailboxes/${mailbox.id}`, "PATCH", { status: "active" }))}
                          size="sm"
                          type="button"
                          variant="outline"
                        >
                          {busy === `resume-${mailbox.id}` ? <LoaderCircle className="animate-spin" /> : <Play />} Reprendre
                        </Button>
                      ) : (
                        <Button
                          disabled={busy === `pause-${mailbox.id}`}
                          onClick={() => act(`pause-${mailbox.id}`, () => api(`/api/admin/prospection/mailboxes/${mailbox.id}`, "PATCH", { status: "paused" }))}
                          size="sm"
                          type="button"
                          variant="outline"
                        >
                          <PauseCircle /> Pause
                        </Button>
                      )}
                      {mailbox.warmup_hold ? (
                        <Button
                          disabled={busy === `hold-${mailbox.id}`}
                          onClick={() => {
                            if (!confirm("Lancer la prospection ? La boîte reprend la montée en charge à 5 e-mails par jour.")) return;
                            act(`hold-${mailbox.id}`, () => api(`/api/admin/prospection/mailboxes/${mailbox.id}`, "PATCH", { warmup_hold: false }));
                          }}
                          size="sm"
                          type="button"
                          variant="outline"
                        >
                          {busy === `hold-${mailbox.id}` ? <LoaderCircle className="animate-spin" /> : <Send />} Lancer la prospection
                        </Button>
                      ) : (
                        <Button
                          disabled={busy === `hold-${mailbox.id}`}
                          onClick={() => act(`hold-${mailbox.id}`, () => api(`/api/admin/prospection/mailboxes/${mailbox.id}`, "PATCH", { warmup_hold: true }))}
                          size="sm"
                          title="Plus aucune prospection depuis cette boîte ; elle continue d’échanger avec vos autres boîtes."
                          type="button"
                          variant="outline"
                        >
                          {busy === `hold-${mailbox.id}` ? <LoaderCircle className="animate-spin" /> : <Flame />} Rester en chauffe
                        </Button>
                      )}
                      <Button
                        disabled={busy === `verify-${mailbox.id}`}
                        onClick={() => act(`verify-${mailbox.id}`, () => api(`/api/admin/prospection/mailboxes/${mailbox.id}`, "PATCH", { verify: true }))}
                        size="sm"
                        aria-label="Revérifier l’accès par le compte de service"
                        title="Revérifier l’accès par le compte de service"
                        type="button"
                        variant="ghost"
                      >
                        <ShieldCheck />
                      </Button>
                      <Button
                        disabled={busy === `warmup-${mailbox.id}`}
                        onClick={() => {
                          if (!confirm("Recommencer la chauffe ? La boîte repasse par la chauffe, sans prospection.")) return;
                          act(`warmup-${mailbox.id}`, () => api(`/api/admin/prospection/mailboxes/${mailbox.id}`, "PATCH", { restart_warmup: true }));
                        }}
                        size="sm"
                        aria-label="Recommencer la chauffe"
                        title="Recommencer la chauffe"
                        type="button"
                        variant="ghost"
                      >
                        <RotateCcw />
                      </Button>
                      <Button
                        className="text-muted-foreground hover:text-destructive"
                        disabled={busy === `delete-${mailbox.id}`}
                        onClick={() => {
                          if (!confirm(`Retirer ${mailbox.email} ? Ses prospects en cours ne seront plus relancés.`)) return;
                          act(`delete-${mailbox.id}`, () => api(`/api/admin/prospection/mailboxes/${mailbox.id}`, "DELETE"));
                        }}
                        size="sm"
                        aria-label="Retirer la boîte"
                        title="Retirer la boîte"
                        type="button"
                        variant="ghost"
                      >
                        <Trash2 />
                      </Button>
                    </span>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
        <AddMailboxForm busy={busy} onAction={act} />
      </AdminPanel>

      {dns.length > 0 && (
        <AdminPanel title="Domaines d’envoi">
          <ul className="m-0 grid list-none gap-4 p-0">
            {dns.map((report) => (
              <li className="grid gap-1.5" key={report.domain}>
                <strong className="text-[13px] font-semibold">{report.domain}</strong>
                {(["spf", "dkim", "dmarc"] as const).map((key) => (
                  <p className="m-0 flex flex-wrap items-baseline gap-2 text-[12.5px]" key={key}>
                    <AdminChip tone={report[key].ok ? "active" : "danger"}>{key.toUpperCase()}</AdminChip>
                    <span className="text-muted-foreground">{report[key].hint}</span>
                  </p>
                ))}
              </li>
            ))}
          </ul>
          <p className="mt-4 mb-0 text-[12px] leading-relaxed text-muted-foreground">
            La réputation des domaines se suit dans Google Postmaster Tools. Un domaine mal noté se retire : l’autre continue,
            et le domaine principal n’est jamais touché.
          </p>
        </AdminPanel>
      )}
    </div>
  );
}

function AddMailboxForm({
  busy,
  onAction,
}: {
  busy: string;
  onAction: (key: string, action: () => Promise<unknown>) => Promise<void>;
}) {
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [cap, setCap] = useState("");

  return (
    <form
      className="flex flex-wrap items-end gap-3 border-t bg-muted/40 px-4 py-3.5"
      onSubmit={(event) => {
        event.preventDefault();
        onAction("add", async () => {
          await api("/api/admin/prospection/mailboxes", "POST", {
            email,
            display_name: name,
            daily_cap: cap ? Number(cap) : null,
          });
          setEmail("");
          setName("");
          setCap("");
        });
      }}
    >
      <div className="grid gap-1.5">
        <Label htmlFor="mailbox-email">Adresse Workspace</Label>
        <Input className="h-9 w-[240px] bg-background" id="mailbox-email" onChange={(event) => setEmail(event.target.value)} placeholder="prenom@domaine-envoi.fr" required type="email" value={email} />
      </div>
      <div className="grid gap-1.5">
        <Label htmlFor="mailbox-name">Nom affiché et signature</Label>
        <Input className="h-9 w-[180px] bg-background" id="mailbox-name" onChange={(event) => setName(event.target.value)} placeholder="Prénom" required value={name} />
      </div>
      <div className="grid gap-1.5">
        <Label htmlFor="mailbox-cap">Plafond par jour</Label>
        <Input className="h-9 w-[110px] bg-background" id="mailbox-cap" max={40} min={1} onChange={(event) => setCap(event.target.value)} placeholder="réglages" type="number" value={cap} />
      </div>
      <Button disabled={busy === "add"} size="sm" type="submit">
        {busy === "add" ? <LoaderCircle className="animate-spin" /> : <Plus />} Ajouter et vérifier l’accès
      </Button>
    </form>
  );
}

function SetupPanel({ configured, clientId }: { configured: boolean; clientId: string | null }) {
  const [copied, setCopied] = useState("");
  const copy = async (key: string, value: string) => {
    await navigator.clipboard.writeText(value);
    setCopied(key);
  };

  return (
    <AdminPanel title="Mise en route de Google Workspace">
      <ol className="m-0 grid gap-2.5 pl-5 text-[12.5px] leading-relaxed">
        <li>
          Créez une organisation Google Workspace <strong className="font-semibold">dédiée à la prospection</strong>, avec un ou
          deux domaines d’envoi proches de la marque, jamais le domaine principal.
        </li>
        <li>Créez un utilisateur par boîte (une personne qui signe de son prénom), avec sa photo de profil.</li>
        <li>
          Dans Google Cloud : un projet, l’API Gmail activée, un <strong className="font-semibold">compte de service</strong> et sa
          clé JSON.
        </li>
        <li>
          Console d’administration Workspace → Sécurité → Accès et contrôle des données → Commandes des API →{" "}
          <strong className="font-semibold">Délégation au niveau du domaine</strong> : autorisez l’identifiant client du compte de
          service pour ces deux champs d’application.
          <span className="mt-1.5 flex flex-wrap items-center gap-2">
            <code className="rounded bg-muted px-1.5 py-0.5 font-mono text-[12px]">{clientId ?? "identifiant client du compte de service"}</code>
            {clientId && (
              <Button onClick={() => copy("client", clientId)} size="sm" type="button" variant="ghost">
                {copied === "client" ? <Check /> : <Copy />} Copier
              </Button>
            )}
          </span>
          <span className="mt-1.5 flex flex-wrap items-center gap-2">
            <code className="rounded bg-muted px-1.5 py-0.5 font-mono text-[12px] break-all">{SCOPES}</code>
            <Button onClick={() => copy("scopes", SCOPES)} size="sm" type="button" variant="ghost">
              {copied === "scopes" ? <Check /> : <Copy />} Copier
            </Button>
          </span>
        </li>
        <li>
          Placez la clé JSON (brute ou en base64) dans la variable d’environnement du serveur{" "}
          <code className="font-mono text-[12px]">PROSPECTION_GOOGLE_SERVICE_ACCOUNT</code>, jamais dans le dépôt.{" "}
          {configured ? <AdminChip tone="active">Détectée</AdminChip> : <AdminChip tone="danger">Absente</AdminChip>}
        </li>
        <li>Publiez SPF, DKIM et DMARC sur chaque domaine, puis ajoutez les boîtes ci-dessous : l’accès est vérifié à l’ajout.</li>
      </ol>
    </AdminPanel>
  );
}
